import { Component, OnDestroy, inject } from "@angular/core";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import {
  DestinationSettingsUiState,
  PublishStateService,
} from "app/js/src/infrastructure/clipping-studio/publish-state.service";
import {
  SocialAccount,
  DestinationConfig,
  DestinationType,
} from "app/js/src/domain/clipping-studio/publish/publish-models";
import { AspectRatio, parseAspectRatio } from "app/js/src/domain/clipping-studio/aspect-ratio";
import { DestinationsRepository } from "app/js/src/infrastructure/repositories/destinations/destinations.repository";
import {
  isFacebookPlatform as isFacebookCatalogPlatform,
  isInstagramPlatform as isInstagramCatalogPlatform,
  isTikTokPlatform as isTikTokCatalogPlatform,
  isYouTubeCatalogPlatform,
  resolveFacebookPublicationSettings,
  resolveInstagramPublicationSettings,
  resolveYouTubePublicationSettings,
  type Destination,
  type DestinationPlatform,
  type DestinationPlatformData,
  type DestinationPlatformWithDestinations,
  type FacebookDestinationPlatformData,
  type FacebookPublicationSettings,
  type InstagramDestinationPlatformData,
  type InstagramPublicationSettings,
  type TikTokCreatorInfo,
  type TikTokDestinationPlatformData,
  type YouTubeDestinationPlatformData,
  type YouTubePublicationSettings,
} from "app/js/src/domain/destinations";
import { UserService } from "app/js/src/domain/user-service";

@Component({
  selector: "evrideo-clipping-studio-destination-tab",
  templateUrl: "./clipping-studio-destination-tab.component.html",
  styleUrls: ["./clipping-studio-destination-tab.component.scss"],
})
export class ClippingStudioDestinationTabComponent implements OnDestroy {
  private destroy$ = new Subject<void>();
  readonly publishState = inject(PublishStateService);
  private destinationsRepository = inject(DestinationsRepository);
  private userService = inject(UserService);

  destinations: SocialAccount[] = [];
  selectedIds: string[] = [];
  configs: Record<string, DestinationConfig> = {};
  expandedId: string | null = null;

  get canViewDestinationSettings(): boolean {
    return !!this.userService.getCurrentUser()?.hasDestinationsViewerPermission;
  }

  get canEditDestinationSettings(): boolean {
    return !!this.userService.getCurrentUser()?.hasDestinationsEditorPermission;
  }

  constructor() {
    this.refreshDestinations();

    this.publishState.selectedDestinations$
      .pipe(takeUntil(this.destroy$))
      .subscribe((ids) => {
        this.selectedIds = ids;
        this.refreshDestinations();
      });
    this.publishState.configs$.pipe(takeUntil(this.destroy$)).subscribe((c) => {
      this.configs = c;
    });
    this.publishState.expandedDestinationId$
      .pipe(takeUntil(this.destroy$))
      .subscribe((id) => {
        this.expandedId = id;
      });

    this.publishState.projectPublishHydrated$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.refreshDestinations();
        this.tryBootstrapPreselectedDestinationSettings();
      });

    this.tryBootstrapPreselectedDestinationSettings();
  }

  private refreshDestinations(): void {
    this.destinations = this.publishState.getDestinationsForUi();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  isSelected(id: string): boolean {
    return this.selectedIds.includes(id);
  }

  isExpanded(id: string): boolean {
    return this.expandedId === id;
  }

  toggleDestination(id: string): void {
    const wasSelected = this.isSelected(id);
    this.publishState.toggleDestination(id);

    // Requirement: settings request should fire when destination is selected,
    // and should not fire when destination is unselected.
    if (!wasSelected) {
      if (!this.publishState.isUnavailableDestination(id)) {
        void this.ensureDestinationSettingsLoaded(id);
      }
      return;
    }

    // When unselecting, drop local cached state so expanded panel doesn't show stale placeholder.
    this.publishState.clearDestinationSettingsUiState(id);
    this.publishState.clearPublishBlockers(`tiktok:${id}`);
    this.publishState.clearTikTokPublishContext(id);
    this.publishState.clearPublishBlockers(`facebook:${id}`);
    this.publishState.clearPublishBlockers(`title:${id}`);
    this.publishState.clearPublishBlockers(`facebook-title:${id}`);
    this.publishState.clearPublishBlockers(`instagram-caption:${id}`);
    if (this.expandedId === id) {
      this.publishState.setExpandedDestination(null);
    }
  }

  toggleExpanded(id: string, e: Event): void {
    e.stopPropagation();
    if (this.publishState.isUnavailableDestination(id)) {
      return;
    }
    this.publishState.setExpandedDestination(
      this.expandedId === id ? null : id,
    );
  }

  getConfig(accountId: string): DestinationConfig {
    return this.publishState.getConfig(accountId);
  }

  updateConfig(accountId: string, updates: Partial<DestinationConfig>): void {
    this.publishState.updateDestinationConfig(accountId, updates);
  }

  onAspectRatioChange(accountId: string, value: string | AspectRatio): void {
    this.publishState.updateDestinationConfig(accountId, {
      aspectRatio: parseAspectRatio(value),
    });
  }

  supportsSidecar(type: DestinationType): boolean {
    return ["yt", "fb", "cdn"].includes(type);
  }

  destinationSettingsState(uid: string): DestinationSettingsUiState {
    return this.publishState.getDestinationSettingsUiState(uid);
  }

  isTikTokPlatform(platform: DestinationPlatform | null): boolean {
    return !!platform && isTikTokCatalogPlatform(platform);
  }

  isFacebookPlatform(platform: DestinationPlatform | null): boolean {
    return !!platform && isFacebookCatalogPlatform(platform);
  }

  isInstagramPlatform(platform: DestinationPlatform | null): boolean {
    return !!platform && isInstagramCatalogPlatform(platform);
  }

  isYouTubePlatform(platform: DestinationPlatform | null): boolean {
    return !!platform && isYouTubeCatalogPlatform(platform);
  }

  hasDisabledTikTokInteractions(info: TikTokCreatorInfo | null): boolean {
    return (
      info?.commentDisabled === true ||
      info?.duetDisabled === true ||
      info?.stitchDisabled === true
    );
  }

  asTikTokPlatformData(
    data: DestinationPlatformData | null,
  ): TikTokDestinationPlatformData | null {
    return data as TikTokDestinationPlatformData | null;
  }

  asYouTubeDestinationDefaults(
    details: { platformData: DestinationPlatformData | null } | null | undefined,
  ): YouTubeDestinationPlatformData | null {
    return (details?.platformData as YouTubeDestinationPlatformData | null) ?? null;
  }

  asYouTubePublicationSettings(
    destinationUid: string,
    destinationDefaults: YouTubeDestinationPlatformData | null,
  ): YouTubePublicationSettings {
    const saved = this.publishState.getDestinationSettingsOverride(destinationUid);
    return resolveYouTubePublicationSettings(saved, destinationDefaults);
  }

  asFacebookDestinationDefaults(
    details: { platformData: DestinationPlatformData | null } | null | undefined,
  ): FacebookDestinationPlatformData | null {
    return (details?.platformData as FacebookDestinationPlatformData | null) ?? null;
  }

  asFacebookPublicationSettings(
    destinationUid: string,
    destinationDefaults: FacebookDestinationPlatformData | null,
  ): FacebookPublicationSettings | null {
    const saved = this.publishState.getDestinationSettingsOverride(destinationUid);
    return resolveFacebookPublicationSettings(saved, destinationDefaults);
  }

  asInstagramDestinationDefaults(
    details: { platformData: DestinationPlatformData | null } | null | undefined,
  ): InstagramDestinationPlatformData | null {
    return (details?.platformData as InstagramDestinationPlatformData | null) ?? null;
  }

  asInstagramPublicationSettings(
    destinationUid: string,
    destinationDefaults: InstagramDestinationPlatformData | null,
  ): InstagramPublicationSettings | null {
    const saved = this.publishState.getDestinationSettingsOverride(destinationUid);
    return resolveInstagramPublicationSettings(saved, destinationDefaults);
  }

  onFacebookSettingsChange(
    destinationUid: string,
    settings: FacebookPublicationSettings,
  ): void {
    const payload = settings as unknown as DestinationPlatformData;
    this.onDestinationSettingsDraftChange(destinationUid, payload);
    this.publishState.setDestinationSettingsOverride(destinationUid, payload);
  }

  onInstagramSettingsChange(
    destinationUid: string,
    settings: InstagramPublicationSettings,
  ): void {
    const payload = settings as unknown as DestinationPlatformData;
    this.onDestinationSettingsDraftChange(destinationUid, payload);
    this.publishState.setDestinationSettingsOverride(destinationUid, payload);
  }

  onYouTubeSettingsChange(
    destinationUid: string,
    settings: YouTubePublicationSettings,
  ): void {
    this.onDestinationSettingsDraftChange(destinationUid, settings);
    this.publishState.setDestinationSettingsOverride(destinationUid, settings);
  }

  private resolvePlatformAndDestination(
    destinationUid: string,
  ): { platform: DestinationPlatform; destination: Destination } | null {
    const platforms: readonly DestinationPlatformWithDestinations[] =
      this.destinationsRepository.getCachedTenantPlatformsWithDestinations();
    for (const row of platforms) {
      const destination = row.destinations.find((d) => d.uid === destinationUid);
      if (destination) {
        return { platform: row.platform, destination };
      }
    }
    return null;
  }

  private tryBootstrapPreselectedDestinationSettings(): void {
    const uids = this.publishState.consumePreselectedUidsPendingBootstrap();
    if (!uids?.length) {
      return;
    }
    const loadable = uids.filter(
      (uid) => !this.publishState.isUnavailableDestination(uid),
    );
    if (!loadable.length) {
      return;
    }
    void this.loadDestinationSettingsForUids(loadable);
  }

  private async loadDestinationSettingsForUids(
    destinationUids: readonly string[],
  ): Promise<void> {
    await Promise.all(
      destinationUids.map((uid) => this.ensureDestinationSettingsLoaded(uid)),
    );
  }

  async ensureDestinationSettingsLoaded(destinationUid: string): Promise<void> {
    if (!this.canViewDestinationSettings) return;
    if (!destinationUid?.trim()) return;
    if (this.publishState.isUnavailableDestination(destinationUid)) return;
    const current = this.publishState.getDestinationSettingsUiState(destinationUid);
    if (current?.panelState === "loading" || current?.panelState === "ready") return;

    const resolved = this.resolvePlatformAndDestination(destinationUid);
    if (!resolved) {
      this.publishState.setDestinationSettingsUiState(destinationUid, {
        ...this.publishState.getDestinationSettingsUiState(destinationUid),
        panelState: "error",
        errorMessage: "Could not resolve destination platform for settings.",
      });
      return;
    }

    if (isTikTokCatalogPlatform(resolved.platform)) {
      this.publishState.setPublishBlockers(`tiktok:${destinationUid}`, [
        {
          code: "tiktok_creator_info_loading",
          message: "Loading TikTok account settings.",
        },
      ]);
    }

    this.publishState.setDestinationSettingsUiState(destinationUid, {
      ...this.publishState.getDestinationSettingsUiState(destinationUid),
      panelState: "loading",
      platform: resolved.platform,
    });

    try {
      const [result, creatorInfo] = await Promise.all([
        this.destinationsRepository.loadDestinationSettings(
          resolved.platform,
          destinationUid,
        ),
        isTikTokCatalogPlatform(resolved.platform)
          ? this.destinationsRepository.getDestinationCreatorInfo(
              resolved.platform.platformId,
              destinationUid,
            )
          : Promise.resolve(null),
      ]);
      this.publishState.setDestinationSettingsUiState(destinationUid, {
        panelState: "ready",
        platform: resolved.platform,
        details: result.details,
        videoCategories: result.videoCategories,
        creatorInfo,
        errorMessage: null,
        draftPlatformData: null,
        isSaving: false,
      });
      if (isTikTokCatalogPlatform(resolved.platform)) {
        this.initializeTikTokPublishGate(destinationUid, resolved.destination, creatorInfo);
      } else if (isFacebookCatalogPlatform(resolved.platform)) {
        this.publishState.syncTitleBlockers();
      } else if (isInstagramCatalogPlatform(resolved.platform)) {
        this.publishState.syncInstagramCaptionBlockers();
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Could not load destination settings.";
      if (isTikTokCatalogPlatform(resolved.platform)) {
        this.publishState.setPublishBlockers(`tiktok:${destinationUid}`, [
          {
            code: "tiktok_creator_info_load_failed",
            message,
          },
        ]);
      }
      this.publishState.setDestinationSettingsUiState(destinationUid, {
        ...this.publishState.getDestinationSettingsUiState(destinationUid),
        panelState: "error",
        platform: resolved.platform,
        errorMessage: message,
      });
    }
  }

  private initializeTikTokPublishGate(
    destinationUid: string,
    destination: Destination,
    creatorInfo: TikTokCreatorInfo | null,
  ): void {
    this.publishState.setTikTokPublishContext({
      destinationUid,
      destinationName: destination.displayName,
      maxVideoPostDurationSec: creatorInfo?.maxVideoPostDurationSec ?? 0,
      isBrandContent: false,
    });

    if (!creatorInfo) {
      this.publishState.setPublishBlockers(`tiktok:${destinationUid}`, [
        {
          code: "tiktok_creator_info_missing",
          message: "TikTok account settings could not be loaded.",
        },
      ]);
      return;
    }

    this.publishState.setPublishBlockers(`tiktok:${destinationUid}`, [
      {
        code: "tiktok_privacy_invalid",
        message: "Select TikTok privacy status.",
      },
    ]);
  }

  onDestinationSettingsDraftChange(
    destinationUid: string,
    platformData: DestinationPlatformData | null,
  ): void {
    this.publishState.updateDestinationSettingsDraft(destinationUid, platformData);
  }

  // Destination settings saving is handled in Tenant settings only.
}
